// @ts-nocheck
import { useEffect, useRef } from 'react';
import { streamGet } from '../lib/http';
import { api } from '../lib/api';
import { getToken } from '../lib/auth';

const MIN_RECONNECT_MS = 60_000;

/**
 * Optional SSE heartbeat when the internal guardian is not running (e.g. FTP PHP).
 * On Docker/NAS the guardian already ticks every 60s — no persistent SSE needed.
 */
export function useSchedulerHeartbeat(enabled = true): void {
  const started = useRef(false);

  useEffect(() => {
    if (!enabled || !getToken() || started.current) return;
    started.current = true;

    let aborted = false;
    let controller = null;
    let reconnectTimer = null;

    const scheduleReconnect = (delayMs = MIN_RECONNECT_MS) => {
      if (aborted || reconnectTimer) return;
      reconnectTimer = setTimeout(() => {
        reconnectTimer = null;
        connect();
      }, delayMs);
    };

    const connect = async () => {
      if (aborted || !getToken()) return;
      try {
        const status = await api.schedulerStatus();
        if (status?.active === true) {
          // Guardian ticking — avoid SSE reconnect storm
          scheduleReconnect(MIN_RECONNECT_MS * 2);
          return;
        }
      } catch {
        scheduleReconnect(MIN_RECONNECT_MS);
        return;
      }

      if (aborted) return;
      controller?.abort?.();
      const { promise, abort } = streamGet('/api/scheduler/heartbeat', () => {
        /* tick events only; ignore legacy reconnect payloads */
      });
      controller = { abort };

      promise.catch(() => {
        if (!aborted) scheduleReconnect(MIN_RECONNECT_MS);
      });
    };

    connect();

    return () => {
      aborted = true;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      controller?.abort?.();
      started.current = false;
    };
  }, [enabled]);
}
